/* eslint-disable @typescript-eslint/no-redundant-type-constituents */
import { getSpecificationPropertyObject } from '../../../utils/utils';
import { ModuleInputType } from '../enums/module-input-type.enum';
import { StepErrorType, StepType } from '../enums/step-type.enum';
import { DropdownWidget, RadioButtonGroupWidget, StringResource } from './module-control-specification.model';
import { ModuleSpecification } from './module-specification.model';
import { ModuleVersion } from './module-version.model';
import {
    ModuleParameter,
    StaticParameter,
    StepOutputParameter,
    StepParameter,
    getStepParameterAccordingToType,
    isAssignable,
    isInputTypeStatic,
} from './parameter.model';
import { SpecificationPropertyObject } from './specification-property-object.model';

export class Step {
    inputParameters: StepParameter[] = [];
    moduleVersion: ModuleVersion;
    flowId: string;
    type: StepType;
    disabled: boolean = false;

    /**
     * Indicates the reason why the step is invalid.
     * if the step is valid -> undefined.
     * this returns only one error, no matter how many occur. Depending on the order they override each other.
     */
    stepError?: StepErrorType;

    /**
     * Map of step errors to their respective error message.
     */
    private errorTranslationKeyMap: Map<StepErrorType, string> = new Map([
        [StepErrorType.NoFilesSelected, 'WORKFLOWS.RUNNER.NOTIFICATION.NO_FILES_SELECTED'],
        [StepErrorType.ParamNotConnected, 'WORKFLOWS.RUNNER.NOTIFICATION.INVALID_PARAMETERS'],
        [StepErrorType.OneOrMoreStaticParameterNotSet, 'WORKFLOWS.RUNNER.NOTIFICATION.STATIC_PARAMETERS_NOT_SET'],
    ]);

    constructor(flowId: string, moduleVersion: ModuleVersion, type: StepType) {
        this.flowId = flowId;
        this.moduleVersion = moduleVersion;
        this.type = type;

        this.addAllModuleParametersToStep();
    }

    get title(): string | undefined {
        return this.moduleVersion.title;
    }

    /**
     * Validation rules:
     *  - For all module (input parameters) we check if we have a StepParameter.
     *  - If yes, Check if the step parameter is a StepOutputParameter. If not, it's always valid (since StaticParameters don't need to be connected)
     *  - If yes, we check if the StepOutputParameter has a connection (meaning a sourceOutputKey)
     */
    isValid(_?: string, connectionsOnly?: boolean): boolean {
        const allStepParametersHaveValues: boolean = this.moduleVersion.inputParameters.every((moduleParam: ModuleParameter) => {
            let isValid = false;

            if (this.type === StepType.Module) {
                const stepParameter: StepParameter | undefined = this.getParameter(moduleParam.key);
                if (stepParameter instanceof StepOutputParameter) {
                    isValid = stepParameter.sourceOutputKey != null;
                    this.stepError = stepParameter.sourceOutputKey != null ? undefined : StepErrorType.ParamNotConnected;
                } else if (stepParameter != null && stepParameter.parameterType === 'static') {
                    if (connectionsOnly || (stepParameter.getValue() != null && stepParameter.getValue() !== '')) {
                        // (Static) Parameter is set -> has a value (which is not null)
                        isValid = true;
                        this.stepError = undefined;
                        if (moduleParam.type === ModuleInputType.ChoiceSingle) {
                            const specProp: SpecificationPropertyObject | undefined = getSpecificationPropertyObject(
                                stepParameter.key,
                                this.moduleVersion.versionReadModel.specification,
                            );

                            const selectionItems: StringResource[] | undefined =
                                specProp && specProp.widgetSpecificUiProperty
                                    ? (specProp.widgetSpecificUiProperty as DropdownWidget | RadioButtonGroupWidget).items
                                    : undefined;

                            if (specProp != null && selectionItems != null) {
                                const foundValuesInUiSpec: StringResource[] = selectionItems.filter(
                                    (item: StringResource) => item.key === stepParameter.getValue(),
                                );
                                if (foundValuesInUiSpec.length !== 1) {
                                    isValid = false;
                                    this.stepError = StepErrorType.OneOrMoreStaticParameterNotSet;
                                }
                            }
                        }
                    } else if (stepParameter != null && stepParameter.parameterType === 'static' && stepParameter.getValue() == null) {
                        // (Static) Parameter is set but has no value (null)
                        this.stepError = StepErrorType.OneOrMoreStaticParameterNotSet;
                        isValid = false;
                    } else {
                        return false;
                    }
                } else if (stepParameter == null) {
                    // When the step parameter is not there, this parameter is not connected
                    isValid = false;
                    this.stepError = StepErrorType.ParamNotConnected;
                }
            } else {
                // ModuleDefineInput and WorkflowDefineInput don't need to be validated.
                if (this.type === StepType.WorkflowDefineInput || this.type === StepType.ModuleDefineInput) {
                    const numberOfFilesDefined: number = this.getNumberOfStepDefinedInputFiles();

                    isValid = numberOfFilesDefined !== 0;
                    this.stepError = numberOfFilesDefined !== 0 ? undefined : StepErrorType.NoFilesSelected;
                } else {
                    isValid = true;
                    this.stepError = undefined;
                }
            }

            return isValid;
        });
        return allStepParametersHaveValues;
    }

    clearInputParameterValue(paramKey: string) {
        const param: StepParameter | undefined = this.getParameter(paramKey);
        if (param != null && param instanceof StaticParameter) {
            param.value = undefined;
        }
    }

    validateStepOutputParameters(thisStep: Step) {
        if (this.flowId === thisStep.flowId) {
            return;
        }

        const deletedOutputParameter: StepParameter[] = this.inputParameters.filter((param: StepParameter) => {
            return (
                param instanceof StepOutputParameter &&
                param.sourceFlowId === thisStep.flowId &&
                thisStep.moduleVersion.getOutputParameter(param.sourceOutputKey) == null
            );
        });

        deletedOutputParameter.forEach((stepOutputParam: StepParameter) => {
            this.removeInputParameterInternal(stepOutputParam, false);
        });
    }

    onOtherStepRemoved(step: Step) {
        this.inputParameters.forEach((param: StepParameter, index: number) => {
            if (param instanceof StepOutputParameter && param.sourceFlowId === step.flowId) {
                this.inputParameters.splice(index, 1);
            }
        });
    }

    setInputParameter(parameter: StepParameter): boolean {
        let stepHasBeenAdded: boolean = false;
        const moduleParameter: ModuleParameter | undefined = this.moduleVersion.getInputParameter(parameter.key);
        if (moduleParameter != null) {
            if (parameter instanceof StepOutputParameter) {
                // Check if types are assignable
                if (isAssignable(parameter.type, moduleParameter.type)) {
                    this.removeInputParameterInternal(parameter, false);
                    this.inputParameters.push(parameter);
                    stepHasBeenAdded = true;
                }
            } else if (parameter instanceof StaticParameter) {
                this.removeInputParameterInternal(parameter, false);
                this.inputParameters.push(parameter);
                stepHasBeenAdded = true;
            }
        }

        return stepHasBeenAdded;
    }

    getParameter(key: string): StepParameter | undefined {
        const paramResults: StepParameter[] = this.inputParameters.filter((p: StepParameter) => key === p.key);

        let param: StepParameter | undefined;
        if (paramResults.length > 1) {
            console.warn('ATTENTION: More than 1 result found for getParameter(). paramKey: ' + key + ', flowId: ' + this.flowId + '. Please check!');
        } else if (paramResults.length === 1) {
            param = paramResults[0];
        }

        return param;
    }

    /**
     * Indicates if the given parameter has a connection
     */
    hasParameterConnection(paramKey: string): boolean {
        let isConnected: boolean = false;

        if (this.getParameter(paramKey) != null) {
            const param: StepParameter = this.getParameter(paramKey)!;
            isConnected = (param as StepOutputParameter).sourceFlowId != null;
        }

        return isConnected;
    }

    removeInputParameter(paramKey: string): void {
        const param: StepParameter | undefined = this.getParameter(paramKey);

        if (param != null) {
            this.removeInputParameterInternal(param, true);
        }
    }

    removeStepOutputParameter(stepParam: StepParameter): void {
        if (stepParam != null && stepParam instanceof StepOutputParameter) {
            // Before removing, check if we need to re-add the staticParameter (if a connection from a number gets removed i.e.)
            const moduleParam: ModuleParameter | undefined = this.moduleVersion.getInputParameter(stepParam.key);

            if (moduleParam != null && isInputTypeStatic(moduleParam.type)) {
                this.removeInputParameterInternal(stepParam, true);
            } else {
                this.removeInputParameterInternal(stepParam, false);
            }
        }
    }

    /**
     * Updates module version and synchronizes (new) moduleVersion parameters with StepParameters.
     */
    updateModuleVersion(moduleVersion: ModuleVersion): void {
        this.moduleVersion = moduleVersion;
        this.synchronizeStepParametersWithModuleVersion();
    }

    isAnyStaticParameterConnected(): boolean {
        return this.inputParameters.some((param: StepParameter) => {
            if (param instanceof StepOutputParameter) {
                const moduleParameter: ModuleParameter | undefined = this.moduleVersion.getInputParameter(param.key);
                if (moduleParameter != null && isInputTypeStatic(moduleParameter.type)) {
                    return true;
                }
            }
            return false;
        });
    }

    /**
     * Returns the number of selected files for define input steps.
     * If the step is not a DefineInput step, returns 0.
     */
    getNumberOfStepDefinedInputFiles(): number {
        if (
            this.type !== StepType.WorkflowDefineInput &&
            this.type !== StepType.ModuleDefineInput &&
            this.type !== StepType.OldWorkflowStarter &&
            this.type !== StepType.OldSingleFileUpload
        ) {
            throw Error('Step not provided or invalid step type was provided!');
        }

        let numberOfFilesDefined: number = 0;
        const staticStepParameters: StepParameter[] = this.inputParameters.filter((param: StepParameter) => param instanceof StaticParameter);
        for (const staticStepParameter of staticStepParameters) {
            const param: StaticParameter<ModuleParameter, unknown> = <StaticParameter<ModuleParameter, unknown>>staticStepParameter;
            if (Array.isArray(param.value)) {
                // we have to provide file_list parameter always, but this caused the #336369 bug
                // because of that we need to check if files in file_list are really provided, and
                // if not we need to keep numberOfFilesDefined as zero
                if (param.value.length > 0) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    numberOfFilesDefined = (param.value as Array<any>).length;
                }
            } else if (param.value) {
                return 1;
            }
        }

        return numberOfFilesDefined;
    }

    getErrorDescriptionKey(): string | undefined {
        if (this.stepError != null) {
            return this.errorTranslationKeyMap.get(this.stepError);
        }
    }

    doesStaticParameterHaveAValidValue(paramKey: string): boolean {
        const stepParam: StepParameter | undefined = this.getParameter(paramKey);
        if (
            stepParam != null &&
            (<StaticParameter<ModuleParameter, unknown>>stepParam).getValue() != null &&
            stepParam instanceof StaticParameter &&
            (<StaticParameter<ModuleParameter, unknown>>stepParam).getValue() !== ''
        ) {
            let isValid = true;

            // 394534: If ModuleParameter.items is defined, check if we find the value within the items. This is an additional check
            // for dropdowns and radiobuttongroups to see if the value is actually valid.
            const moduleParameterItems: StringResource[] | undefined = stepParam.moduleParameter.items;
            if (moduleParameterItems != null) {
                isValid = moduleParameterItems.find((item: StringResource) => item.key === stepParam.getValue()) != null;
            }
            return isValid;
        }
        return false;
    }

    /**
     * Checks if the module parameters are still in sync with the step. This method is called when the moduleVersion is changed in a step.
     * There are 2 different scenarios:
     *  - Module Parameter has been removed (on ModuleVersion change): If a stepOutput parameter has been removed,
     *      we need to check all the other steps if it was connected.
     *  - Module parameter has been added (on ModuleVersion change): If it's a static parameter, we add it. If it's stepOutput, we do nothing
     */
    private synchronizeStepParametersWithModuleVersion(): void {
        // param is removed from moduleVersion
        this.inputParameters.forEach((stepParam: StepParameter) => {
            if (this.moduleVersion.getInputParameter(stepParam.key) == null) {
                this.removeInputParameterInternal(stepParam, false);
            }
        });

        // param is added to moduleVersion: If it's step output, we don't care
        // (because we just 'store' the values in the step)
        this.moduleVersion.inputParameters.forEach((moduleParam: ModuleParameter) => {
            if (this.getParameter(moduleParam.key) == null) {
                if (isInputTypeStatic(moduleParam.type)) {
                    this.addModuleParameterToStep(moduleParam);
                }
            }
        });
    }

    private addAllModuleParametersToStep() {
        const parameters: ModuleParameter[] = this.moduleVersion.inputParameters;
        for (const param of parameters) {
            if (isInputTypeStatic(param.type)) {
                this.addModuleParameterToStep(param);
            }
        }
    }

    private addModuleParameterToStep(moduleParam: ModuleParameter): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const defaultValue: any | undefined = this.getDefaultValueOutOfSpec(moduleParam.key);
        const parameter: StepParameter = getStepParameterAccordingToType(moduleParam, defaultValue);
        this.inputParameters.push(parameter);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private getDefaultValueOutOfSpec(key: string): any | undefined {
        const specification: ModuleSpecification | undefined = this.moduleVersion.specification;
        if (specification != null) {
            if (specification.spec.inputs[key] != null && specification.spec.inputs[key].default != null) {
                return specification.spec.inputs[key].default;
            }
        }
    }

    /**
     * Removes an Input Parameter from the Step.
     *
     * @param param - the parameter to remove
     * @param addStaticParameterIfNeeded - {boolean} if a StepOutputParameter has been removed which has an underlying StaticParameter
     * (i.e. a connection that goes to a Static Parameter), then we have to re-add the static parameter.
     */
    private removeInputParameterInternal(param: StepParameter, addStaticParameterIfNeeded: boolean): void {
        // For Static Parameters we just find by key (because the key is unique per Step)
        const index = this.inputParameters.findIndex((p: StepParameter) => param.key === p.key);

        if (index >= 0) {
            this.inputParameters.splice(index, 1);

            if (addStaticParameterIfNeeded && param instanceof StepOutputParameter) {
                const moduleParameter: ModuleParameter | undefined = this.moduleVersion.getInputParameter(param.key);
                if (moduleParameter != null && isInputTypeStatic(moduleParameter.type)) {
                    const parameter: StepParameter = getStepParameterAccordingToType(moduleParameter);
                    this.inputParameters.push(parameter);
                }
            }
        }
    }
}
